import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  marketingEconomicsApi,
  type MarketingDimension,
  type MarketingEconomics,
  type MarketingEconomicsFilters,
  type MarketingEconomicsRow,
  type MarketingPractice,
} from '../api/marketingEconomics'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Callout, Card, CardContent, CardHeader, DataTable, FilterBar, LoadingState, Page, PageHeader,
  StatCard, StatGrid, StatusPill, Tabs, TextField,
} from '../components/ui'

/**
 * C-04 — ROI e margine del marketing per campagna, fornitore e fonte, con drill-down fino alla pratica.
 * Dietro il flag `report.roi_marketing`. Il costo è quello delle spese con campagna; ROI vuoto ("—") se la spesa è zero.
 */
const eur = (n: number | null | undefined) =>
  n == null ? '—' : '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const pct = (n: number | null) => (n == null ? '—' : (n * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 }) + ' %')

type Tab = MarketingDimension

const TABS: { id: Tab; label: string; first: string }[] = [
  { id: 'campaign', label: 'Per campagna', first: 'Campagna' },
  { id: 'provider', label: 'Per fornitore', first: 'Fornitore' },
  { id: 'source', label: 'Per fonte', first: 'Fonte' },
]

export function MarketingEconomicsPage() {
  const enabled = useFeatureFlag('report.roi_marketing')
  const [form, setForm] = useState<MarketingEconomicsFilters>({})
  const [applied, setApplied] = useState<MarketingEconomicsFilters>({})
  const [data, setData] = useState<MarketingEconomics | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [tab, setTab] = useState<Tab>('campaign')
  const [open, setOpen] = useState<{ dimension: MarketingDimension; row: MarketingEconomicsRow } | null>(null)
  const [practices, setPractices] = useState<MarketingPractice[] | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await marketingEconomicsApi.report(applied))
    } catch (e) {
      setData(null)
      setError(e instanceof Error ? e.message : 'Impossibile caricare il report.')
    } finally {
      setLoading(false)
    }
  }, [applied])

  useEffect(() => {
    if (enabled) void load()
  }, [enabled, load])

  useEffect(() => {
    setOpen(null)
    setPractices(null)
  }, [applied, tab])

  const openRow = async (row: MarketingEconomicsRow) => {
    setOpen({ dimension: tab, row })
    setPractices(null)
    try {
      setPractices((await marketingEconomicsApi.drilldown(applied, tab, row.key)).items)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile caricare le pratiche.')
    }
  }

  const setField = (k: keyof MarketingEconomicsFilters, v: string) => setForm(f => ({ ...f, [k]: v }))
  const field = (k: keyof MarketingEconomicsFilters, label: string, type = 'text') => (
    <div key={k}>
      <TextField label={label} type={type} value={form[k] ?? ''} onChange={e => setField(k, e.target.value)} />
    </div>
  )

  if (!enabled) {
    return (
      <Page>
        <PageHeader title="ROI marketing" icon="query_stats" />
        <Callout tone="info">Funzione non attiva.</Callout>
      </Page>
    )
  }

  const rows = data ? (tab === 'campaign' ? data.byCampaign : tab === 'provider' ? data.byProvider : data.bySource) : []
  const t = data?.totals

  return (
    <Page>
      <PageHeader title="ROI marketing" icon="query_stats" />
      <FilterBar onSubmit={() => setApplied({ ...form })} onReset={() => { setForm({}); setApplied({}) }}>
        {field('from', 'Da', 'date')}
        {field('to', 'A', 'date')}
        {field('source', 'Fonte')}
        {field('campaignId', 'Campagna (id)', 'number')}
        {field('leadProviderId', 'Fornitore (id)', 'number')}
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}
      {loading && <LoadingState />}

      {data && t && (
        <>
          <StatGrid>
            <StatCard label="Spesa" value={eur(t.spend)} />
            <StatCard label="Lead" value={String(t.leads)} />
            <StatCard label="Pratiche" value={`${t.practices} (${t.activations} attivate)`} />
            <StatCard label="Ricavo maturato" value={eur(t.accrued)} />
            <StatCard label="Margine" value={eur(t.margin)} />
            <StatCard label="ROI" value={pct(t.roi)} />
          </StatGrid>

          <Tabs aria-label="Dimensione" value={tab} onChange={id => setTab(id as Tab)} items={TABS.map(x => ({ id: x.id, label: x.label }))} />
          <Card>
            <CardContent>
              <DataTable>
                <thead>
                  <tr>
                    <th>{TABS.find(x => x.id === tab)!.first}</th>
                    <th className="text-end">Spesa</th>
                    <th className="text-end">Lead</th>
                    <th className="text-end">Pratiche</th>
                    <th className="text-end">Attivate</th>
                    <th className="text-end">Previsto</th>
                    <th className="text-end">Maturato</th>
                    <th className="text-end">Incassato</th>
                    <th className="text-end">Provvigioni</th>
                    <th className="text-end">Margine</th>
                    <th className="text-end">ROI</th>
                    <th className="text-end">Ricavo/lead</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={12}>Nessun dato nel periodo.</td></tr>
                  )}
                  {rows.map(r => (
                    <tr key={r.key} onClick={() => void openRow(r)} style={{ cursor: 'pointer' }}>
                      <td>{r.label}</td>
                      <td className="text-end md-num">{eur(r.spend)}</td>
                      <td className="text-end md-num">{r.leads}</td>
                      <td className="text-end md-num">{r.practices}</td>
                      <td className="text-end md-num">{r.activations}</td>
                      <td className="text-end md-num">{eur(r.expected)}</td>
                      <td className="text-end md-num">{eur(r.accrued)}</td>
                      <td className="text-end md-num">{eur(r.collected)}</td>
                      <td className="text-end md-num">{eur(r.commissions)}</td>
                      <td className="text-end md-num">{eur(r.margin)}</td>
                      <td className="text-end md-num">{pct(r.roi)}</td>
                      <td className="text-end md-num">{eur(r.revenuePerLead)}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            </CardContent>
          </Card>

          {open && (
            <Card>
              <CardHeader title={`Pratiche — ${open.row.label}`} />
              <CardContent>
                {practices === null ? (
                  <LoadingState />
                ) : practices.length === 0 ? (
                  <p>Nessuna pratica per questa riga.</p>
                ) : (
                  <DataTable>
                    <thead>
                      <tr>
                        <th>Pratica</th><th>Stato</th>
                        <th className="text-end">Previsto</th><th className="text-end">Maturato</th>
                        <th className="text-end">Incassato</th><th className="text-end">Provvigioni</th>
                        <th className="text-end">Margine</th>
                      </tr>
                    </thead>
                    <tbody>
                      {practices.map(p => (
                        <tr key={p.contractId}>
                          <td><Link to={`/contracts/${p.contractId}`}>{p.referenceCode}</Link></td>
                          <td><StatusPill tone={p.activated ? 'success' : 'neutral'}>{p.activated ? 'Attivata' : 'Non attivata'}</StatusPill></td>
                          <td className="text-end md-num">{eur(p.expected)}</td>
                          <td className="text-end md-num">{eur(p.accrued)}</td>
                          <td className="text-end md-num">{eur(p.collected)}</td>
                          <td className="text-end md-num">{eur(p.commissions)}</td>
                          <td className="text-end md-num">{eur(p.margin)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
                )}
              </CardContent>
            </Card>
          )}

          <Callout tone="info">
            <ul>{data.limitations.map(l => <li key={l}>{l}</li>)}</ul>
          </Callout>
        </>
      )}
    </Page>
  )
}
